<template>
  <v-app-bar 
    id="app-bar" 
    app 
    elevation="8" 
    height="70"
    class="app-bar-gradient"
  >
    <!-- Menu Toggle Button -->
    <v-btn
      color="rgba(255,255,255,0.9)"
      class="menu-toggle mr-3"
      elevation="2"
      fab
      x-small
      @click="setDrawer(!drawer)"
    >
      <v-icon color="primary" size="20">
        {{ drawer ? 'mdi-menu-open' : 'mdi-menu' }}
      </v-icon>
    </v-btn>

    <!-- App Title/Route Name -->
    <div class="app-title-section">
      <v-toolbar-title class="app-title">
        <div class="d-flex align-center">
          <v-icon color="white" class="mr-2" size="28">mdi-shield-account</v-icon>
          <div>
            <div class="title-main">HRVEC</div>
            <div class="title-sub">{{ formatRouteName($route.name) }}</div>
          </div>
        </div>
      </v-toolbar-title>
    </div>

    <v-spacer />

    <!-- Notification Bell -->
    <v-menu offset-y left v-if="userProfile.aut === '1' || userProfile.aut === 1">
      <template v-slot:activator="{ on, attrs }">
        <v-btn
          icon
          class="notification-btn mr-2"
          v-bind="attrs"
          v-on="on"
          :loading="loading"
        >
          <v-badge
            :content="notifications.length"
            :value="notifications.length > 0"
            color="error"
            overlap
          >
            <v-icon color="white" size="24">mdi-bell</v-icon>
          </v-badge>
        </v-btn>
      </template>
      <v-card class="notification-card" min-width="350">
        <v-card-title class="primary white--text">
          <v-icon left color="white">mdi-bell</v-icon>
          การแจ้งเตือน
          <v-spacer></v-spacer>
          <v-chip small color="white" text-color="primary">{{ notifications.length }}</v-chip>
        </v-card-title>
        <v-list>
          <v-list-item
            v-for="(notification, index) in notifications"
            :key="index"
            @click="handleNotificationClick(notification)"
          >
            <v-list-item-avatar>
              <v-icon color="primary">mdi-information</v-icon>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title>{{ notification.college_name }}</v-list-item-title>
              <v-list-item-subtitle>{{ notification.personnel_temporary_notifyDetail }}</v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action>
              <v-chip x-small :color="notification.type">{{ notification.personnel_temporary_notifyDateTIme }}</v-chip>
              <v-btn icon><v-icon color="red" @click="delNotify(notification.personnel_temporary_notifyID)">mdi-delete</v-icon></v-btn>
            </v-list-item-action>
          </v-list-item>
          <v-divider v-if="notifications.length === 0"></v-divider>
          <v-list-item v-if="notifications.length === 0">
            <v-list-item-content class="text-center">
              <v-icon size="48" color="grey lighten-2">mdi-bell-off</v-icon>
              <div class="text-subtitle-1 grey--text">ไม่มีการแจ้งเตือน</div>
            </v-list-item-content>
          </v-list-item>
        </v-list>
        <v-card-actions v-if="notifications.length > 0">
          <v-spacer></v-spacer>
          <v-btn text color="primary" @click="clearAllNotifications">
            ล้างทั้งหมด
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-menu>

    <!-- User Profile Menu -->
    <v-menu offset-y left>
      <template v-slot:activator="{ on, attrs }">
        <v-btn
          class="user-profile-btn mr-2"
          v-bind="attrs"
          v-on="on"
          text
        >
          <v-avatar size="36" class="mr-2">
            <v-img
              v-if="userProfile.avatar"
              :src="userProfile.avatar"
              alt="Profile"
            ></v-img>
            <v-icon v-else color="white" size="20">mdi-account-circle</v-icon>
          </v-avatar>
          <div class="user-info d-none d-md-block">
            <div class="user-name">{{ userProfile.name || 'ผู้ใช้งาน' }}</div>
            <div class="user-role white--text">{{ userProfile.role || 'Admin' }}</div>
          </div>
          <v-icon color="white" class="ml-1">mdi-chevron-down</v-icon>
        </v-btn>
      </template>
      
      <v-card min-width="280">
        <v-card-text class="text-center pa-4">
          <v-avatar size="64" class="mb-3">
            <v-img
              v-if="userProfile.avatar"
              :src="userProfile.avatar"
              alt="Profile"
            ></v-img>
            <v-icon v-else color="primary" size="40">mdi-account-circle</v-icon>
          </v-avatar>
          <div class="text-h6 font-weight-bold">{{ userProfile.name || 'ผู้ใช้งาน' }}</div>
          <div class="text-subtitle-2 grey--text">{{ userProfile.email || 'user@example.com' }}</div>
          <v-chip small :color="userProfile.status === 'online' ? 'success' : 'grey'" class="mt-2">
            <v-icon left small>{{ userProfile.status === 'online' ? 'mdi-circle' : 'mdi-circle-outline' }}</v-icon>
            {{ userProfile.status === 'online' ? 'ออนไลน์' : 'ออฟไลน์' }}
          </v-chip>
        </v-card-text>
        
        <v-divider></v-divider>
        
        <v-list>            
          <v-list-item @click="goToSettings">
            <v-list-item-icon>
              <v-icon color="primary">mdi-cog</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>ตั้งค่า</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          
          <v-list-item @click="toggleTheme">
            <v-list-item-icon>
              <v-icon color="primary">{{ isDark ? 'mdi-brightness-7' : 'mdi-brightness-4' }}</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ isDark ? 'โหมดสว่าง' : 'โหมดมืด' }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-card>
    </v-menu>

    <!-- Navigation Buttons -->
    <div class="nav-buttons">
      <v-btn       
        class="nav-btn mr-2"
        text
        :to="'/admin/Dashboard'"
        exact
      >
        <v-icon left color="white">mdi-view-dashboard</v-icon>
        <span class="d-none d-lg-inline white--text">Dashboard</span>
      </v-btn>
      
      <v-btn      
        class="nav-btn mr-2"
        text
        :to="'/admin/home_menu'"
        exact
      >
        <v-icon left color="white">mdi-apps</v-icon>
        <span class="d-none d-lg-inline white--text">เมนูระบบ</span>
      </v-btn>
    </div>

    <!-- Logout Button -->
    <v-btn
      color="rgba(244, 67, 54, 0.8)"
      class="logout-btn ml-3"
      elevation="2"
      @click="showLogoutDialog = true"
    >
      <v-icon left color="white">mdi-logout</v-icon>
      <span class="d-none d-md-inline">ออกจากระบบ</span>
    </v-btn>

    <!-- Logout Confirmation Dialog -->
    <v-dialog v-model="showLogoutDialog" max-width="400">
      <v-card class="logout-dialog">
        <v-card-title class="warning white--text">
          <v-icon left color="white">mdi-logout</v-icon>
          ยืนยันการออกจากระบบ
        </v-card-title>
        
        <v-card-text class="pa-6">
          <div class="text-center">
            <v-icon size="64" color="warning" class="mb-4">mdi-help-circle-outline</v-icon>
            <div class="mb-2">คุณต้องการออกจากระบบหรือไม่?</div>
            <div class="grey--text">ข้อมูลที่ยังไม่ได้บันทึกอาจสูญหาย</div>
          </div>
        </v-card-text>
        
        <v-card-actions class="pa-6">
          <v-spacer></v-spacer>
          <v-btn
            text
            color="grey"
            @click="showLogoutDialog = false"
          >
            ยกเลิก
          </v-btn>
          <v-btn
            color="error"
            @click="confirmLogout"
            class="ml-2"
          >
            <v-icon left>mdi-logout</v-icon>
            ออกจากระบบ
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Loading Overlay -->
    <v-overlay v-if="isLoggingOut" absolute>
      <v-progress-circular
        indeterminate
        size="64"
        color="white"
      ></v-progress-circular>
      <div class="text-center mt-4">
        <div class="white--text">กำลังออกจากระบบ...</div>
      </div>
    </v-overlay>
  </v-app-bar>
</template>

<script>
// Components
import { VHover, VListItem } from "vuetify/lib";

// Utilities
import { mapState, mapMutations } from "vuex";

export default {
  name: "DashboardUserAppBar",

  components: {
    AppBarItem: {
      render(h) {
        return h(VHover, {
          scopedSlots: {
            default: ({ hover }) => {
              return h(
                VListItem,
                {
                  attrs: this.$attrs,
                  class: {
                    "primary--text": !hover,
                    "white--text primary elevation-4": hover
                  },
                  props: {
                    activeClass: "primary white--text",
                    dark: hover,
                    link: true,
                    ...this.$attrs
                  }
                },
                this.$slots.default
              );
            }
          }
        });
      }
    }
  },

  props: {
    value: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    showLogoutDialog: false,
    isLoggingOut: false,
    isDark: false,
      ApiKey: "HRvec2021",
    
    // Enhanced notifications with more realistic data
    notifications: [],
    loading: false,
    // User profile data
    userProfile: {
      name: "ผู้ดูแลระบบ",
      email: "",
      role: "ผู้ดูแลระบบ",
      avatar: null, // Can be set to an image URL
      status: "online",
      aut: "admin"
    }
  }),

  computed: {
    ...mapState(["drawer"])
  },

  mounted() {
    this.loadUserProfile();
    this.personnel_temporary_notifyQueryAll();
    this.isDark = this.$vuetify.theme.dark;
  },

  methods: {
    delNotify(id) {
      this.$http.post("personnel_temporary_notify.delete.php", {
        personnel_temporary_notifyID: id,
        ApiKey: this.ApiKey,
      }).then(() => {
        this.notifications = this.notifications.filter(n => n.personnel_temporary_notifyID !== id);
      }).catch(() => {
        console.error("Error deleting notification");
      });

    },
     async personnel_temporary_notifyQueryAll() {
      this.loading = true;
      try {
        const result = await this.$http.post("personnel_temporary_notify.php", {
          ApiKey: this.ApiKey
        });
        this.notifications = result.data;
      } catch (error) {
        this.showSnackbar('เกิดข้อผิดพลาดในการโหลดข้อมูล', 'error');
      } finally {
        this.loading = false;
      }
    },



    ...mapMutations({
      setDrawer: "SET_DRAWER"
    }),

    formatRouteName(routeName) {
      if (!routeName) return 'ระบบจัดการ';
      
      const routeNames = {
        'Dashboard': 'แดชบอร์ด',
        'home_menu': 'เมนูหลัก',
        'personnel_temporary_notify': 'การแจ้งเตือนบุคลากร',
        'rate_work_std02': 'สถิตินักเรียนนักศึกษา',
        'personnel_etc': 'รายงานอัตรากำลัง',
        'temporarycollegeemreg': 'ประกาศรับสมัคร',
        'temporaryemployees': 'ผู้สมัครงาน'
      };
      
      return routeNames[routeName] || routeName;
    },

    loadUserProfile() {
      // Load user profile from session storage or API
      const userSession = JSON.parse(sessionStorage.getItem("user")) || {};
      if (userSession) {
        this.userProfile = {
          ...this.userProfile,
          name: userSession.user_firstname || userSession.user_name || this.userProfile.name,
          email: userSession.user_email || this.userProfile.email,
          role: userSession.user_status || this.userProfile.role,
          aut: userSession.userProfile || this.userProfile.aut,
        };
      }
    },

    handleNotificationClick(notification) {
      // Handle notification click
      // You can add navigation logic here
    },

    clearAllNotifications() {
      this.notifications = [];
    },

    goToProfile() {
      // Navigate to profile page
      this.$router.push('/admin/profile');
    },

    goToSettings() {
      // Navigate to settings page
      this.$router.push('/admin/setting');
    },

    toggleTheme() {
      this.isDark = !this.isDark;
      this.$vuetify.theme.dark = this.isDark;
      // Save theme preference
      localStorage.setItem('darkTheme', this.isDark);
    },

    async confirmLogout() {
      this.isLoggingOut = true;
      this.showLogoutDialog = false;
      
      try {
        // Simulate logout process
        await new Promise(resolve => setTimeout(resolve, 1500));        
        // Clear session data
        sessionStorage.clear();
        localStorage.removeItem('authToken');           
  
        this.$router.push("/");
      } catch (error) {
        console.error('Logout error:', error);
      } finally {
        this.isLoggingOut = false;
      }
    },

    // Legacy logout method for compatibility
    logout() {
      this.showLogoutDialog = true;
    }
  }
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Prompt:wght@300;400;500;600;700&display=swap");

.app-bar-gradient {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  backdrop-filter: blur(10px);
  border-bottom: 3px solid rgba(255, 255, 255, 0.1);
  font-family: 'Prompt', sans-serif;
}

.menu-toggle {
  background: rgba(255, 255, 255, 0.9) !important;
  transition: all 0.3s ease;
}

.menu-toggle:hover {
  background: rgba(255, 255, 255, 1) !important;
  transform: scale(1.05);
}

.app-title-section {
  margin-left: 16px;
}

.app-title {
  color: white !important;
  font-family: 'Prompt', sans-serif;
}

.title-main {
  font-size: 1.3rem;
  font-weight: 600;
  line-height: 1.2;
}

.title-sub {
  font-size: 0.85rem;
  opacity: 0.9;
  font-weight: 300;
}

.notification-btn {
  background: rgba(255, 255, 255, 0.1) !important;
  transition: all 0.3s ease;
}

.notification-btn:hover {
  background: rgba(255, 255, 255, 0.2) !important;
  transform: scale(1.05);
}

.notification-card {
  border-radius: 12px !important;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.user-profile-btn {
  background: rgba(255, 255, 255, 0.1) !important;
  border-radius: 25px !important;
  padding: 8px 16px !important;
  transition: all 0.3s ease;
}

.user-profile-btn:hover {
  background: rgba(255, 255, 255, 0.2) !important;
  transform: translateY(-1px);
}

.user-info {
  text-align: left;
  margin-left: 8px;
  margin-right: 8px;
}

.user-name {
  font-size: 0.9rem;
  font-weight: 500;
  color: white;
  line-height: 1.2;
}

.user-role {
  font-size: 0.75rem;
  line-height: 1;
}

.profile-card {
  border-radius: 12px !important;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.nav-buttons .nav-btn {
  border-radius: 20px !important;
  transition: all 0.3s ease;
  backdrop-filter: blur(5px);
}

.nav-buttons .nav-btn:hover {
  background: rgba(255, 255, 255, 0.2) !important;
  transform: translateY(-1px);
}

.nav-buttons .nav-btn.v-btn--active {
  background: rgba(255, 255, 255, 0.25) !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.logout-btn {
  border-radius: 20px !important;
  transition: all 0.3s ease;
  backdrop-filter: blur(5px);
}

.logout-btn:hover {
  background: rgba(244, 67, 54, 1) !important;
  transform: translateY(-1px);
  box-shadow: 0 4px 15px rgba(244, 67, 54, 0.4);
}

.logout-dialog {
  border-radius: 15px !important;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .app-title-section {
    margin-left: 8px;
  }
  
  .title-main {
    font-size: 1.1rem;
  }
  
  .title-sub {
    display: none;
  }
  
  .user-profile-btn {
    padding: 4px 8px !important;
  }
  
  .nav-buttons {
    display: none;
  }
}

@media (max-width: 480px) {
  .notification-btn {
    margin-right: 4px !important;
  }
  
  .user-profile-btn {
    margin-right: 4px !important;
  }
  
  .logout-btn {
    margin-left: 8px !important;
    padding: 8px !important;
  }
}

/* Animation for notifications */
.v-badge >>> .v-badge__badge {
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
}

/* Dark theme support */
.theme--dark .notification-card,
.theme--dark .profile-card,
.theme--dark .logout-dialog {
  background: #2d2d2d !important;
}

/* Loading overlay styling */
.v-overlay >>> .v-overlay__content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

/* Smooth transitions */
* {
  transition: all 0.3s ease;
}

/* Custom scrollbar for notification list */
.notification-card >>> .v-list {
  max-height: 300px;
  overflow-y: auto;
}

.notification-card >>> .v-list::-webkit-scrollbar {
  width: 4px;
}

.notification-card >>> .v-list::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.notification-card >>> .v-list::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.notification-card >>> .v-list::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>